import type {
  CheckboxFilterGroups,
  CheckboxFilterItem,
  FilterableState,
  Pagination,
} from 'types/filters';
import type dayjs from 'utils/dayjs';
import moment from 'utils/dayjs';
import {
  dateFormatForAPI,
  formatDate as defaultFormatDate,
} from '../../utils/date';

export const formatDate = (
  date?: dayjs.ConfigType,
  format = dateFormatForAPI
) => defaultFormatDate(date, format);

// `as const` so `action.type` comparisons against these narrow to literals.
export const filterActions = {
  ENABLE_FILTERS: 'ENABLE_FILTERS',
  DISABLE_FILTERS: 'DISABLE_FILTERS',
  SET_KEYWORD_FILTER: 'SET_KEYWORD_FILTER',
  SET_TYPE: 'SET_TYPE',
  APPLY_FILTERS: 'APPLY_FILTERS',
  CHECK_ALL_FILTERS: 'CHECK_ALL_FILTERS',
  UNCHECK_FILTERS: 'UNCHECK_FILTERS',
  CLEAR_FILTERS: 'CLEAR_FILTERS',
  SET_SELECTED_ITEM: 'SET_SELECTED_ITEM',
  SET_START_DATE: 'SET_START_DATE',
  SET_END_DATE: 'SET_END_DATE',
} as const;

// Every field the filter actions carry. They share one payload type rather than
// forming a discriminated union because this reducer is composed with each
// screen's own reducer via `combineReducers`, so it is also handed actions it
// does not recognise and has to pass them through.
export interface FiltersActionPayload {
  startDate?: dayjs.ConfigType;
  endDate?: dayjs.ConfigType;
  keyword?: string;
  type?: string;
  filterKey?: string;
  filterName?: string;
  selectedValue?: string | number;
  checked?: boolean;
  checkBoxFilterNames?: string[];
}

export interface FiltersAction {
  type: string;
  payload?: FiltersActionPayload;
}

// The fields a case may overwrite. `filters` stays `object` because each screen
// has its own facets, and the concrete type is restored by the `as S` below.
interface FiltersPatch {
  filters?: object;
  apiCheckedDefaultFilters?: object;
  pagination?: Pagination;
  filtersEnabled?: boolean;
  applyFilters?: boolean;
  loading?: boolean;
}

// Filter groups are addressed by a runtime key, so this widens a screen's
// concrete filters type to the record shape those lookups need. The keys come
// from the screen that owns the filters, so they always resolve to a group.
const groupsOf = (filters: object): CheckboxFilterGroups =>
  filters as unknown as CheckboxFilterGroups;

const setAppliedValues = (
  checkBoxFilterNames: string[],
  filters: object
): CheckboxFilterGroups =>
  Object.fromEntries(
    checkBoxFilterNames.map(filterName => [
      filterName,
      groupsOf(filters)[filterName].map((filterItem: CheckboxFilterItem) => ({
        ...filterItem,
        applied: !!(filterItem.checked && filterItem.visible),
      })),
    ])
  );

export const filtersReducer = <
  S extends FilterableState,
  A extends FiltersAction,
>(
  state: S,
  action: A
): S => {
  const payload = action.payload;

  // A date action carries only the date it changes, so the other end of the
  // range falls back to what is already set. Without that fallback the
  // isSameOrAfter check in SET_START_DATE below is unreachable and picking a
  // start date always clears the end date.
  // `|| undefined` so an unset ('') date reads as absent, and formatDate falls
  // back to today exactly as before.
  const {
    startDate = state.filters?.startDate || undefined,
    endDate = state.filters?.endDate || undefined,
  } = payload ?? {};

  // Each case builds the fields to overwrite; they are merged onto the state
  // once at the end. The `as S` is needed because TypeScript types a spread of
  // a generic as `S & {...}` rather than `S`.
  const patch = ((): FiltersPatch | null => {
    switch (action.type) {
      case filterActions.SET_START_DATE:
        return {
          pagination: { ...state.pagination, currentPage: 1 },
          filters: {
            ...state.filters,
            startDate: formatDate(startDate),
            endDate:
              endDate && moment(endDate).isSameOrAfter(startDate)
                ? formatDate(endDate)
                : '',
          },
        };

      case filterActions.SET_END_DATE:
        return {
          pagination: { ...state.pagination, currentPage: 1 },
          filters: { ...state.filters, endDate: formatDate(endDate) },
        };

      case filterActions.SET_SELECTED_ITEM: {
        const filterKey = payload!.filterKey!;
        const selectedValue = String(payload!.selectedValue);
        const group = groupsOf(state.filters)[filterKey];

        return {
          filters: {
            ...state.filters,
            latestAppliedFilter: filterKey,
            [filterKey]: [
              ...group.filter(
                filterItem =>
                  !!filterItem.value &&
                  filterItem.value.toString() !== selectedValue
              ),
              {
                ...group.find(
                  filterItem => filterItem.value?.toString() === selectedValue
                ),
                checked: payload!.checked,
              },
            ],
          },
        };
      }

      case filterActions.ENABLE_FILTERS:
        return { filtersEnabled: true };

      case filterActions.APPLY_FILTERS:
        return {
          loading: true,
          applyFilters: true,
          pagination: { ...state.pagination, currentPage: 1 },
          filters: {
            ...state.filters,
            ...setAppliedValues(payload!.checkBoxFilterNames!, state.filters),
          },
        };

      case filterActions.DISABLE_FILTERS:
        return {
          filtersEnabled: false,
          loading: true,
          applyFilters: false,
          filters: { ...state.apiCheckedDefaultFilters },
        };

      case filterActions.CLEAR_FILTERS:
        return {
          loading: true,
          filters: { ...state.apiCheckedDefaultFilters },
        };

      case filterActions.UNCHECK_FILTERS:
        return {
          filters: {
            ...state.filters,
            [payload!.filterName!]: groupsOf(state.filters)[
              payload!.filterName!
            ].map(filterItem => ({ ...filterItem, checked: false })),
          },
        };

      case filterActions.CHECK_ALL_FILTERS:
        return {
          filters: {
            ...state.filters,
            [payload!.filterName!]: groupsOf(state.filters)[
              payload!.filterName!
            ].map(filterItem => ({
              ...filterItem,
              checked: filterItem.visible,
            })),
          },
        };

      case filterActions.SET_KEYWORD_FILTER:
        return {
          filters: { ...state.filters, keywordSearch: payload!.keyword },
        };

      case filterActions.SET_TYPE:
        return { filters: { ...state.filters, type: payload!.type } };

      default:
        return null;
    }
  })();

  return patch ? ({ ...state, ...patch } as S) : state;
};
